import { useAppSelector } from "./reduxHooks";
import {
  selectAllRoles,
  selectDrawerSelectedAccountId,
  selectDashboardUserEmail,
  selectSpecificSettings,
  selectAccountsState
} from "app/data/selectors";
import {
  canEditMileageDeviationStatus,
  canEditMileageDeviationComments,
  canEditMileageDeviationStatusBySettings,
  canEditMileageDeviationCommentsBySettings,
  getUserRoleNameFromSettings,
  isDriver,
  isDriverBySettings,
  UserSettings
} from "app/data/helpers/rolePermissions";

/**
 * Custom hook to get current user information and permissions
 * Centralizes access to user context, roles, and account information
 * Uses both legacy roles array and new userSettings API for permissions
 */
export const useCurrentUser = () => {
  // Get user information from Redux store
  const roles = useAppSelector(selectAllRoles);
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const userEmail = useAppSelector(selectDashboardUserEmail);
  const userSettingsRaw = useAppSelector(selectSpecificSettings);
  const { accountUser } = useAppSelector(selectAccountsState);

  // Type cast userSettings to our interface (since Redux selector returns 'object')
  // Also validate that it has the expected properties
  const userSettings =
    userSettingsRaw &&
    typeof userSettingsRaw === "object" &&
    "role" in userSettingsRaw
      ? (userSettingsRaw as UserSettings)
      : null;

  // Get current user role name from userSettings
  const currentRoleName = getUserRoleNameFromSettings(
    userSettings,
    roles,
    accountUser
  );

  const permissionsBySettings = userSettings
    ? {
        canEditMileageDeviationStatus: canEditMileageDeviationStatusBySettings(
          userSettings,
          roles,
          accountUser
        ),
        canEditMileageDeviationComments:
          canEditMileageDeviationCommentsBySettings(
            userSettings,
            roles,
            accountUser
          )
      }
    : null;

  // Fallback to legacy role-based permissions if userSettings not available
  const permissionsByRoles = {
    canEditMileageDeviationStatus: canEditMileageDeviationStatus(roles),
    canEditMileageDeviationComments: canEditMileageDeviationComments(roles)
  };

  // Use userSettings permissions if available, otherwise fallback to roles
  const permissions = permissionsBySettings || permissionsByRoles;

  // Check if user is a Driver (restricted role)
  const isDriverUser = userSettings
    ? isDriverBySettings(userSettings, roles, accountUser)
    : isDriver(roles);

  return {
    // User information
    userEmail,
    accountId,
    roles,
    userSettings,
    currentRoleName,

    // Permissions
    permissions,

    // Convenience flags
    isAdmin: permissions.canEditMileageDeviationStatus,
    hasEditAccess: permissions.canEditMileageDeviationComments,
    isDriver: isDriverUser,

    // Debug info
    debugInfo: {
      hasUserSettings: !!userSettings,
      roleUniqueId: userSettings?.role,
      usingSettingsPermissions: !!permissionsBySettings
    }
  };
};

export default useCurrentUser;
